import React, { useMemo } from 'react';
import clsx from 'clsx';
import styles from './PrintContactInfo.module.scss';
import { Vet } from '@spike/vet-model';
import { formatPhoneNumber } from '@spike/phone-utils';
import Client, { Contact } from '@spike/client-model';

type Props = {
    client: Client;
    vet?: Vet;
    countryId: string;
    className?: string;
};

type SectionVM = {
    key: 'authorized' | 'emergency' | 'vet';
    label: string;
    nameLine: string;
    phone: string;
};

const fullName = (first?: string, last?: string) => `${first ?? ''} ${last ?? ''}`.trim();

const relationshipLabel = (c?: Contact) => (c?.relationship?.name ? ` (${c.relationship.name})` : '');

const pickAuthorized = (contacts: Contact[]) => contacts[0];
const pickEmergency = (contacts: Contact[]) => contacts.find(c => c.emergencyContact);

export const PrintContactInfo: React.FC<Props> = ({ client, vet, countryId, className }) => {
    const sections = useMemo<SectionVM[]>(() => {
        const contacts = client.authorizedContacts ?? [];
        const authorized = pickAuthorized(contacts);
        const emergency = pickEmergency(contacts);

        const authorizedName = authorized
            ? `${fullName(authorized.firstName, authorized.lastName)}${relationshipLabel(authorized)}`
            : 'Not provided';

        const emergencyName = emergency
            ? `${fullName(emergency.firstName, emergency.lastName)}${relationshipLabel(emergency)}`
            : 'Not provided';

        const vetName = vet ? vet.name : 'Not provided';

        return [
            {
                key: 'authorized',
                label: 'AUTHORIZED CONTACT',
                nameLine: authorizedName,
                phone: authorized?.phone ? formatPhoneNumber(authorized.phone, countryId) : ''
            },
            {
                key: 'emergency',
                label: 'EMERGENCY CONTACT',
                nameLine: emergencyName,
                phone: emergency?.phone ? formatPhoneNumber(emergency.phone, countryId) : ''
            },
            {
                key: 'vet',
                label: 'VET',
                nameLine: vetName,
                phone: vet?.phone ? formatPhoneNumber(vet.phone, countryId) : ''
            }
        ];
    }, [client.authorizedContacts, vet, countryId]);

    return (
        <div className={clsx(styles.root, className)}>
            {sections.map(s => (
                <div
                    key={s.key}
                    className={styles.col}
                >
                    <div className={styles.label}>{s.label}</div>

                    <div className={styles.name}>{s.nameLine}</div>

                    {!!s.phone && (
                        <div className={styles.phoneContent}>
                            <span className={styles.phone}>{s.phone}</span>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default PrintContactInfo;
